import { Controller } from "@hotwired/stimulus"
import { toPng } from "html-to-image"

export default class extends Controller {
  static targets = [
    "preview", "placeholder",
    "modeHtmlTab", "modePartialTab", "htmlFields", "partialFields",
    "htmlInput", "partialPath", "variables",
    "scenePreset", "frameWidth", "frameHeight",
    "perspective", "perspectiveValue",
    "rotateX", "rotateXValue",
    "rotateY", "rotateYValue",
    "scale", "scaleValue",
    "grain", "grainValue",
    "pickerBtn", "pickerStatus", "selectedList",
    "htmlTree", "htmlTreeBody", "previewArea", "resizeHandle",
    "shadowX", "shadowXValue",
    "shadowY", "shadowYValue",
    "shadowBlur", "shadowBlurValue",
    "shadowSpread", "shadowSpreadValue",
    "shadowColor", "shadowOpacity", "shadowOpacityValue",
    "captureStatus"
  ]

  connect() {
    this.debounceTimer = null
    this.currentMode = "html"
    this.pickerActive = false
    this.selectedElements = []
    this.pickIdCounter = 0
    this.hoverLabel = null
    this.customShadow = null
    this.presetPerspectiveRaw = null
    this.treeNodeMap = new WeakMap()  // iframe element -> tree node div
    this.treeElementMap = new WeakMap()  // tree node div -> iframe element
    this.treeIdCounter = 0
    this.resizing = false
    this.highlightedTreeNode = null

    // Bound handlers for resize
    this.boundOnResizeMove = this.onResizeMove.bind(this)
    this.boundOnResizeEnd = this.onResizeEnd.bind(this)

    // Bound handlers for iframe events (so we can remove them)
    this.boundOnHover = this.onHover.bind(this)
    this.boundOnHoverOut = this.onHoverOut.bind(this)
    this.boundOnPick = this.onPick.bind(this)

    // Clear selections when iframe reloads
    this.previewTarget.addEventListener("load", () => {
      this.clearSelections()
      if (this.pickerActive) this.disablePicker()
    })
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    if (this.pickerActive) this.disablePicker()
    document.removeEventListener("mousemove", this.boundOnResizeMove)
    document.removeEventListener("mouseup", this.boundOnResizeEnd)
  }

  // ── Mode switching ──

  switchMode(event) {
    this.currentMode = event.currentTarget.dataset.mode

    if (this.currentMode === "html") {
      this.htmlFieldsTarget.classList.remove("hidden")
      this.partialFieldsTarget.classList.add("hidden")
      this.modeHtmlTabTarget.classList.add("tab-active")
      this.modePartialTabTarget.classList.remove("tab-active")
    } else {
      this.htmlFieldsTarget.classList.add("hidden")
      this.partialFieldsTarget.classList.remove("hidden")
      this.modeHtmlTabTarget.classList.remove("tab-active")
      this.modePartialTabTarget.classList.add("tab-active")
    }
  }

  // ── Scene Presets ──

  applyPreset() {
    const select = this.scenePresetTarget
    const presetsJson = select.dataset.presets
    if (!presetsJson) {
      this.updatePreview()
      return
    }

    let presetsData
    try {
      presetsData = JSON.parse(presetsJson)
    } catch {
      this.updatePreview()
      return
    }

    const preset = presetsData[select.value]
    if (!preset) {
      this.updatePreview()
      return
    }

    if (this.hasPerspectiveTarget && preset.perspective != null) {
      if (preset.perspective === "none") {
        this.presetPerspectiveRaw = "none"
        this.perspectiveTarget.value = 1200
      } else {
        this.presetPerspectiveRaw = null
        this.perspectiveTarget.value = parseInt(preset.perspective, 10)
      }
      if (this.hasPerspectiveValueTarget) {
        this.perspectiveValueTarget.textContent = preset.perspective
      }
    }

    if (this.hasRotateXTarget && preset.rotate_x != null) {
      this.rotateXTarget.value = parseInt(preset.rotate_x, 10)
      if (this.hasRotateXValueTarget) {
        this.rotateXValueTarget.textContent = preset.rotate_x
      }
    }

    if (this.hasRotateYTarget && preset.rotate_y != null) {
      this.rotateYTarget.value = parseInt(preset.rotate_y, 10)
      if (this.hasRotateYValueTarget) {
        this.rotateYValueTarget.textContent = preset.rotate_y
      }
    }

    if (this.hasScaleTarget && preset.scale != null) {
      this.scaleTarget.value = parseFloat(preset.scale)
      if (this.hasScaleValueTarget) {
        this.scaleValueTarget.textContent = preset.scale
      }
    }

    if (this.hasGrainTarget && preset.grain_opacity != null) {
      this.grainTarget.value = parseFloat(preset.grain_opacity)
      if (this.hasGrainValueTarget) {
        this.grainValueTarget.textContent = preset.grain_opacity
      }
    }

    this.customShadow = null
    this.resetShadowSliders()

    this.updatePreview()
  }

  resetShadowSliders() {
    if (this.hasShadowXTarget) this.shadowXTarget.value = 0
    if (this.hasShadowYTarget) this.shadowYTarget.value = 25
    if (this.hasShadowBlurTarget) this.shadowBlurTarget.value = 50
    if (this.hasShadowSpreadTarget) this.shadowSpreadTarget.value = -12
    if (this.hasShadowColorTarget) this.shadowColorTarget.value = "#000000"
    if (this.hasShadowOpacityTarget) this.shadowOpacityTarget.value = 0.5

    if (this.hasShadowXValueTarget) this.shadowXValueTarget.textContent = "0px"
    if (this.hasShadowYValueTarget) this.shadowYValueTarget.textContent = "25px"
    if (this.hasShadowBlurValueTarget) this.shadowBlurValueTarget.textContent = "50px"
    if (this.hasShadowSpreadValueTarget) this.shadowSpreadValueTarget.textContent = "-12px"
    if (this.hasShadowOpacityValueTarget) this.shadowOpacityValueTarget.textContent = "0.5"
  }

  // ── Preview rendering ──

  debouncedPreview() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.updatePreview(), 600)
  }

  async updatePreview() {
    if (this.currentMode === "html") {
      await this.renderHtmlMode()
    } else {
      this.renderPartialMode()
    }
  }

  async renderHtmlMode() {
    const html = this.htmlInputTarget.value.trim()
    if (!html) return

    const formData = new FormData()
    formData.append("html", html)
    this.appendSceneParams(formData)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch("/admin/marketing_screenshots/preview_html", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: formData
    })

    const responseHtml = await response.text()
    this.previewTarget.srcdoc = responseHtml
    this.showPreview()
  }

  renderPartialMode() {
    const partialPath = this.partialPathTarget.value.trim()
    if (!partialPath) return

    const params = new URLSearchParams()
    params.set("partial_path", partialPath)
    this.appendSceneSearchParams(params)

    const variables = this.variablesTarget.value.trim()
    if (variables) {
      try {
        JSON.parse(variables)
        params.set("variables", variables)
      } catch (e) {
        // Invalid JSON — skip, don't block preview
      }
    }

    this.previewTarget.src = `/admin/marketing_screenshots/preview?${params.toString()}`
    this.showPreview()
  }

  // ── Element Picker ──

  togglePicker() {
    if (this.pickerActive) {
      this.disablePicker()
    } else {
      this.enablePicker()
    }
  }

  enablePicker() {
    const doc = this.getIframeDoc()
    if (!doc) return

    this.pickerActive = true
    this.pickerBtnTarget.classList.add("btn-active")

    if (this.hasPickerStatusTarget) {
      this.pickerStatusTarget.textContent = "Click elements to select them"
    }

    // Inject picker styles into iframe
    const style = doc.createElement("style")
    style.id = "screenshot-picker-styles"
    style.textContent = `
      .screenshot-picker-hover {
        outline: 2px dashed #3b82f6 !important;
        outline-offset: 2px !important;
        cursor: crosshair !important;
      }
      .screenshot-hover-label {
        position: fixed;
        background: #1e293b;
        color: #e2e8f0;
        font-size: 11px;
        font-family: monospace;
        padding: 2px 6px;
        border-radius: 4px;
        pointer-events: none;
        z-index: 99999;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      }
    `
    doc.head.appendChild(style)

    // Create hover label
    this.hoverLabel = doc.createElement("div")
    this.hoverLabel.className = "screenshot-hover-label"
    this.hoverLabel.style.display = "none"
    doc.body.appendChild(this.hoverLabel)

    // Set cursor on the cinematic content area
    const content = doc.querySelector(".cinematic-content")
    if (content) content.style.cursor = "crosshair"

    // Add event listeners
    doc.addEventListener("mouseover", this.boundOnHover, true)
    doc.addEventListener("mouseout", this.boundOnHoverOut, true)
    doc.addEventListener("click", this.boundOnPick, true)

    // Build and show HTML tree
    this.buildHtmlTree()
    this.showHtmlTree()
  }

  disablePicker() {
    const doc = this.getIframeDoc()

    this.pickerActive = false
    this.pickerBtnTarget.classList.remove("btn-active")

    if (this.hasPickerStatusTarget) {
      this.pickerStatusTarget.textContent = ""
    }

    if (!doc) return

    // Remove picker styles
    const style = doc.getElementById("screenshot-picker-styles")
    if (style) style.remove()

    // Remove hover label
    if (this.hoverLabel && this.hoverLabel.parentNode) {
      this.hoverLabel.remove()
    }
    this.hoverLabel = null

    // Restore cursor
    const content = doc.querySelector(".cinematic-content")
    if (content) content.style.cursor = ""

    // Remove event listeners
    doc.removeEventListener("mouseover", this.boundOnHover, true)
    doc.removeEventListener("mouseout", this.boundOnHoverOut, true)
    doc.removeEventListener("click", this.boundOnPick, true)

    // Remove any lingering hover highlights
    doc.querySelectorAll(".screenshot-picker-hover").forEach(el => {
      el.classList.remove("screenshot-picker-hover")
    })

    // Hide HTML tree
    this.hideHtmlTree()
  }

  onHover(e) {
    const el = e.target
    if (this.isPickerInfrastructure(el)) return

    el.classList.add("screenshot-picker-hover")

    if (this.hoverLabel) {
      this.hoverLabel.textContent = this.getElementLabel(el)
      this.hoverLabel.style.display = "block"
      this.hoverLabel.style.left = (e.clientX + 12) + "px"
      this.hoverLabel.style.top = (e.clientY + 12) + "px"
    }

    // Bidirectional sync: highlight corresponding tree node
    this.highlightTreeNodeForElement(el)
  }

  onHoverOut(e) {
    const el = e.target
    el.classList.remove("screenshot-picker-hover")

    if (this.hoverLabel) {
      this.hoverLabel.style.display = "none"
    }

    // Bidirectional sync: remove tree node highlight
    this.unhighlightTreeNode()
  }

  onPick(e) {
    e.preventDefault()
    e.stopPropagation()

    const el = e.target
    if (this.isPickerInfrastructure(el)) return

    // Don't pick the same element twice
    if (el.dataset.screenshotPickId) return

    const id = String(++this.pickIdCounter)
    el.dataset.screenshotPickId = id

    this.selectedElements.push({
      id,
      element: el,
      label: this.getElementLabel(el),
      state: "normal",
      dimOpacity: 0.15,
      isolateOpacity: 0.08,
      liftAmount: 20
    })

    // Remove hover highlight from picked element
    el.classList.remove("screenshot-picker-hover")

    this.renderSelectedList()
  }

  // ── Element Actions ──

  hideElement(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (entry.state === "hidden") {
      entry.element.style.removeProperty("display")
      entry.state = "normal"
    } else {
      this.resetElementStyle(entry)
      entry.element.style.setProperty("display", "none", "important")
      entry.state = "hidden"
    }
    this.renderSelectedList()
  }

  dimElement(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (entry.state === "dimmed") {
      entry.element.style.removeProperty("opacity")
      entry.state = "normal"
    } else {
      this.resetElementStyle(entry)
      entry.element.style.setProperty("opacity", String(entry.dimOpacity), "important")
      entry.state = "dimmed"
    }
    this.renderSelectedList()
  }

  updateDimOpacity(e) {
    const id = e.currentTarget.dataset.pickId
    const value = parseFloat(e.currentTarget.value)
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry || entry.state !== "dimmed") return

    entry.dimOpacity = value
    entry.element.style.setProperty("opacity", String(value), "important")

    // Update display value without re-rendering (avoids losing slider focus)
    const valueLabel = e.currentTarget.nextElementSibling
    if (valueLabel) valueLabel.textContent = value
  }

  isolateElement(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (entry.state === "isolated") {
      this.removeIsolation()
      entry.state = "normal"
    } else {
      // Reset any other isolated element first
      this.removeIsolation()
      this.resetElementStyle(entry)
      this.applyIsolation(entry.element, entry.isolateOpacity)
      entry.state = "isolated"
    }
    this.renderSelectedList()
  }

  updateIsolateOpacity(e) {
    const id = e.currentTarget.dataset.pickId
    const value = parseFloat(e.currentTarget.value)
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry || entry.state !== "isolated") return

    entry.isolateOpacity = value
    const doc = this.getIframeDoc()
    if (!doc) return

    doc.querySelectorAll(".screenshot-dimmed-by-isolate").forEach(el => {
      el.style.setProperty("opacity", String(value), "important")
    })

    const valueLabel = e.currentTarget.nextElementSibling
    if (valueLabel) valueLabel.textContent = value
  }

  liftElement(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (entry.state === "lifted") {
      this.removeLift(entry)
      entry.state = "normal"
    } else {
      this.resetElementStyle(entry)
      this.applyLift(entry)
      entry.state = "lifted"
    }
    this.renderSelectedList()
  }

  updateLiftAmount(e) {
    const id = e.currentTarget.dataset.pickId
    const value = parseFloat(e.currentTarget.value)
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry || entry.state !== "lifted") return

    entry.liftAmount = value
    this.removeLift(entry)
    entry.state = "lifted"
    this.applyLift(entry)

    const valueLabel = e.currentTarget.nextElementSibling
    if (valueLabel) valueLabel.textContent = value
  }

  // ── Enhanced Element Actions ──

  overrideBackground(e) {
    const id = e.currentTarget.dataset.pickId
    const value = e.currentTarget.value
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (!entry.savedStyles) entry.savedStyles = {}
    if (entry.savedStyles.backgroundColor === undefined) {
      entry.savedStyles.backgroundColor = entry.element.style.backgroundColor || ""
    }
    entry.element.style.setProperty("background-color", value, "important")
  }

  overrideTextColor(e) {
    const id = e.currentTarget.dataset.pickId
    const value = e.currentTarget.value
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (!entry.savedStyles) entry.savedStyles = {}
    if (entry.savedStyles.color === undefined) {
      entry.savedStyles.color = entry.element.style.color || ""
    }
    entry.element.style.setProperty("color", value, "important")
  }

  overrideBorderRadius(e) {
    const id = e.currentTarget.dataset.pickId
    const value = parseFloat(e.currentTarget.value)
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (!entry.savedStyles) entry.savedStyles = {}
    if (entry.savedStyles.borderRadius === undefined) {
      entry.savedStyles.borderRadius = entry.element.style.borderRadius || ""
    }
    entry.element.style.setProperty("border-radius", value + "px", "important")

    const valueLabel = e.currentTarget.nextElementSibling
    if (valueLabel) valueLabel.textContent = value + "px"
  }

  overridePadding(e) {
    const id = e.currentTarget.dataset.pickId
    const value = parseFloat(e.currentTarget.value)
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    if (!entry.savedStyles) entry.savedStyles = {}
    if (entry.savedStyles.padding === undefined) {
      entry.savedStyles.padding = entry.element.style.padding || ""
    }
    entry.element.style.setProperty("padding", value + "px", "important")

    const valueLabel = e.currentTarget.nextElementSibling
    if (valueLabel) valueLabel.textContent = value + "px"
  }

  editElementText(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    entry.editing = true
    if (entry.originalText === undefined) {
      entry.originalText = entry.element.textContent
    }
    this.renderSelectedList()
  }

  saveElementText(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    const textarea = e.currentTarget.closest(".space-y-2")?.querySelector("textarea[data-text-edit]")
    if (textarea) {
      entry.element.textContent = textarea.value
      entry.label = this.getElementLabel(entry.element)
    }
    entry.editing = false
    this.renderSelectedList()
  }

  removeFromDom(e) {
    const id = e.currentTarget.dataset.pickId
    const index = this.selectedElements.findIndex(s => s.id === id)
    if (index === -1) return

    const entry = this.selectedElements[index]
    this.resetElementStyle(entry)
    if (entry.liftClone) entry.liftClone.remove()
    if (entry.annotation) entry.annotation.remove()
    entry.element.remove()
    this.selectedElements.splice(index, 1)
    this.renderSelectedList()
  }

  duplicateElement(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    const clone = entry.element.cloneNode(true)
    delete clone.dataset.screenshotPickId
    entry.element.parentNode.insertBefore(clone, entry.element.nextSibling)
  }

  annotateElement(e) {
    const id = e.currentTarget.dataset.pickId
    const entry = this.selectedElements.find(s => s.id === id)
    if (!entry) return

    const doc = this.getIframeDoc()
    if (!doc) return

    if (entry.annotation) {
      entry.annotation.remove()
      entry.annotation = null
      this.renderSelectedList()
      return
    }

    const iframeWindow = this.previewTarget.contentWindow
    const elRect = entry.element.getBoundingClientRect()
    const highlight = doc.createElement("div")
    highlight.classList.add("screenshot-annotation")
    highlight.style.cssText = [
      `position: fixed`,
      `left: ${elRect.left - 4}px`,
      `top: ${elRect.top - 4}px`,
      `width: ${elRect.width + 8}px`,
      `height: ${elRect.height + 8}px`,
      `border: 3px solid #ef4444`,
      `border-radius: ${iframeWindow.getComputedStyle(entry.element).borderRadius || "4px"}`,
      `pointer-events: none`,
      `z-index: 99997`,
      `box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.2)`
    ].join("; ")

    doc.body.appendChild(highlight)
    entry.annotation = highlight
    this.renderSelectedList()
  }

  removePickedElement(e) {
    const id = e.currentTarget.dataset.pickId
    const index = this.selectedElements.findIndex(s => s.id === id)
    if (index === -1) return

    const entry = this.selectedElements[index]
    this.resetElementStyle(entry)
    if (entry.state === "isolated") this.removeIsolation()
    if (entry.liftClone) {
      entry.liftClone.remove()
      entry.liftClone = null
    }
    if (entry.annotation) {
      entry.annotation.remove()
      entry.annotation = null
    }
    delete entry.element.dataset.screenshotPickId
    this.selectedElements.splice(index, 1)
    this.renderSelectedList()
  }

  resetAll() {
    const doc = this.getIframeDoc()
    if (!doc) return

    // Restore each entry via resetElementStyle (preserves pre-existing inline styles)
    this.selectedElements.forEach(entry => {
      this.resetElementStyle(entry)
      if (entry.annotation) {
        entry.annotation.remove()
        entry.annotation = null
      }
      delete entry.element.dataset.screenshotPickId
    })

    // Clean up any orphaned lift clones / annotations
    doc.querySelectorAll(".screenshot-lift-clone").forEach(el => el.remove())
    doc.querySelectorAll(".screenshot-annotation").forEach(el => el.remove())

    this.selectedElements = []
    this.renderSelectedList()
  }

  // ── PNG Capture ──

  async captureStage(options = {}) {
    const doc = this.getIframeDoc()
    if (!doc) return null

    const stage = doc.querySelector(".cinematic-stage")
    if (!stage) return null

    this.setCaptureStatus("Preparing capture...")

    // Build image data map from original (loaded) images
    const imageMap = this.buildImageDataMap(stage)

    // Create off-screen capture container in main document
    const captureContainer = document.createElement("div")
    captureContainer.style.cssText = "position: fixed; left: -99999px; top: -99999px; pointer-events: none; z-index: -9999;"

    // Copy all <style> elements from iframe head (cinematic styles)
    doc.querySelectorAll("head > style").forEach(styleEl => {
      captureContainer.appendChild(styleEl.cloneNode(true))
    })

    // Create wrapper carrying CSS custom properties from iframe body
    const bodyStyleAttr = doc.body.getAttribute("style") || ""
    const wrapper = document.createElement("div")
    wrapper.setAttribute("style", bodyStyleAttr)
    wrapper.style.position = "relative"
    wrapper.style.display = "flex"
    wrapper.style.alignItems = "center"
    wrapper.style.justifyContent = "center"
    wrapper.style.overflow = "hidden"

    // Set explicit dimensions based on frame size
    const frameW = parseInt(doc.body.style.getPropertyValue("--frame-width"), 10) || 1280
    const frameH = parseInt(doc.body.style.getPropertyValue("--frame-height"), 10) || 800
    const scaleVal = parseFloat(doc.body.style.getPropertyValue("--scene-scale")) || 0.85
    const padding = 120
    wrapper.style.width = Math.round(frameW * scaleVal + padding * 2) + "px"
    wrapper.style.height = Math.round(frameH * scaleVal + padding * 2) + "px"

    if (options.transparent) {
      wrapper.style.setProperty("--scene-background", "transparent")
      wrapper.style.setProperty("--scene-glow-color", "transparent")
      wrapper.style.background = "transparent"
    } else {
      wrapper.style.background = doc.body.style.getPropertyValue("--scene-background") || "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
    }

    // Deep-clone the stage
    const stageClone = stage.cloneNode(true)
    stageClone.style.width = "100%"
    stageClone.style.height = "100%"

    // Remove any picker infrastructure from clone
    stageClone.querySelectorAll(".screenshot-hover-label, .screenshot-lift-clone, .screenshot-annotation, [id='screenshot-picker-styles']").forEach(el => el.remove())
    stageClone.querySelectorAll("[data-screenshot-pick-id]").forEach(el => {
      delete el.dataset.screenshotPickId
    })

    wrapper.appendChild(stageClone)

    // Clone grain overlay (unless transparent).
    // Pre-render the SVG noise to a canvas PNG because the original SVG
    // contains filter='url(#noise)' which html-to-image's URL regex
    // misinterprets as a fetchable resource, causing 404s.
    if (!options.transparent) {
      const grain = doc.querySelector(".cinematic-grain")
      if (grain) {
        const grainClone = grain.cloneNode(true)
        grainClone.style.position = "absolute"
        const grainPng = await this.renderGrainToPng()
        if (grainPng) {
          grainClone.style.backgroundImage = `url(${grainPng})`
        }
        wrapper.appendChild(grainClone)
      }
    }

    // Add override styles for transparent mode
    const overrideStyle = document.createElement("style")
    overrideStyle.textContent = options.transparent
      ? `.cinematic-stage::before { display: none !important; }`
      : ""
    captureContainer.appendChild(overrideStyle)
    captureContainer.appendChild(wrapper)
    document.body.appendChild(captureContainer)

    // Apply image data URIs to cloned images
    this.applyImageDataMap(stageClone, imageMap)

    this.setCaptureStatus("Rendering PNG...")

    // Temporarily disable cross-origin link stylesheets to prevent
    // html-to-image from throwing SecurityError on cssRules access
    const disabledLinks = []
    document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
      try {
        const sheet = link.sheet
        if (sheet) { sheet.cssRules }
      } catch {
        link.disabled = true
        disabledLinks.push(link)
      }
    })

    try {
      const dataUrl = await toPng(wrapper, {
        backgroundColor: options.transparent ? null : undefined,
        pixelRatio: 2,
        skipFonts: true,
        cacheBust: false,
        includeQueryParams: false
      })
      this.setCaptureStatus("")
      return dataUrl
    } catch (err) {
      console.error("PNG capture failed:", err)
      this.setCaptureStatus("Capture failed — check console")
      return null
    } finally {
      disabledLinks.forEach(link => { link.disabled = false })
      captureContainer.remove()
    }
  }

  buildImageDataMap(rootElement) {
    const TRANSPARENT_PIXEL = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQABNjN9GQAAAABJRElEQkSuQmCC"
    const map = new Map()
    const images = rootElement.querySelectorAll("img")

    for (const img of images) {
      if (!img.src || img.src.startsWith("data:")) continue
      if (map.has(img.src)) continue

      try {
        const canvas = document.createElement("canvas")
        canvas.width = img.naturalWidth || img.width || 1
        canvas.height = img.naturalHeight || img.height || 1
        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0)
        map.set(img.src, canvas.toDataURL("image/png"))
      } catch {
        map.set(img.src, TRANSPARENT_PIXEL)
      }
    }

    return map
  }

  applyImageDataMap(clone, imageMap) {
    clone.querySelectorAll("img").forEach(img => {
      if (!img.src || img.src.startsWith("data:")) return
      const dataUri = imageMap.get(img.src)
      if (dataUri) img.src = dataUri
    })
  }

  renderGrainToPng() {
    return new Promise(resolve => {
      const svg = `<svg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>`
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement("canvas")
        canvas.width = 256
        canvas.height = 256
        canvas.getContext("2d").drawImage(img, 0, 0, 256, 256)
        resolve(canvas.toDataURL("image/png"))
      }
      img.onerror = () => resolve(null)
      img.src = "data:image/svg+xml," + encodeURIComponent(svg)
    })
  }

  async downloadPng() {
    const dataUrl = await this.captureStage()
    if (dataUrl) this.triggerDownload(dataUrl, "screenshot.png")
  }

  async downloadTransparentPng() {
    const dataUrl = await this.captureStage({ transparent: true })
    if (dataUrl) this.triggerDownload(dataUrl, "screenshot-transparent.png")
  }

  async copyPngToClipboard() {
    const dataUrl = await this.captureStage()
    if (!dataUrl) return

    try {
      const response = await fetch(dataUrl)
      const blob = await response.blob()
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob })
      ])
      this.setCaptureStatus("Copied to clipboard!")
      setTimeout(() => this.setCaptureStatus(""), 2000)
    } catch (err) {
      console.error("Copy to clipboard failed:", err)
      this.setCaptureStatus("Copy failed — try downloading instead")
      setTimeout(() => this.setCaptureStatus(""), 3000)
    }
  }

  setCaptureStatus(text) {
    if (this.hasCaptureStatusTarget) {
      this.captureStatusTarget.textContent = text
    }
  }

  triggerDownload(dataUrl, filename) {
    const link = document.createElement("a")
    link.download = filename
    link.href = dataUrl
    link.click()
  }

  // ── Shadow Customization ──

  updateShadow() {
    const shadow = this.composeShadowValue()
    this.customShadow = shadow

    // Update value displays
    if (this.hasShadowXValueTarget) this.shadowXValueTarget.textContent = this.shadowXTarget.value + "px"
    if (this.hasShadowYValueTarget) this.shadowYValueTarget.textContent = this.shadowYTarget.value + "px"
    if (this.hasShadowBlurValueTarget) this.shadowBlurValueTarget.textContent = this.shadowBlurTarget.value + "px"
    if (this.hasShadowSpreadValueTarget) this.shadowSpreadValueTarget.textContent = this.shadowSpreadTarget.value + "px"
    if (this.hasShadowOpacityValueTarget) this.shadowOpacityValueTarget.textContent = this.shadowOpacityTarget.value

    // Inject into iframe for live preview
    const doc = this.getIframeDoc()
    if (doc && doc.body) {
      doc.body.style.setProperty("--scene-shadow", shadow)
    }
  }

  composeShadowValue() {
    const x = this.hasShadowXTarget ? this.shadowXTarget.value : 0
    const y = this.hasShadowYTarget ? this.shadowYTarget.value : 25
    const blur = this.hasShadowBlurTarget ? this.shadowBlurTarget.value : 50
    const spread = this.hasShadowSpreadTarget ? this.shadowSpreadTarget.value : -12
    const hex = this.hasShadowColorTarget ? this.shadowColorTarget.value : "#000000"
    const opacity = this.hasShadowOpacityTarget ? this.shadowOpacityTarget.value : 0.5

    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)

    return `${x}px ${y}px ${blur}px ${spread}px rgba(${r}, ${g}, ${b}, ${opacity})`
  }

  // ── Isolation Logic ──

  applyIsolation(targetEl, opacity = 0.08) {
    const doc = this.getIframeDoc()
    if (!doc) return

    // Walk up from the target element, dimming all siblings at each level
    let current = targetEl
    while (current.parentElement) {
      const parent = current.parentElement
      Array.from(parent.children).forEach(sibling => {
        if (sibling === current) return
        if (this.isPickerInfrastructure(sibling)) return
        sibling.classList.add("screenshot-dimmed-by-isolate")
        sibling.style.setProperty("opacity", String(opacity), "important")
      })
      current = parent
    }
  }

  removeIsolation() {
    const doc = this.getIframeDoc()
    if (!doc) return

    doc.querySelectorAll(".screenshot-dimmed-by-isolate").forEach(el => {
      el.classList.remove("screenshot-dimmed-by-isolate")
      el.style.removeProperty("opacity")
    })

    // Also reset state of any isolated entry
    this.selectedElements.forEach(entry => {
      if (entry.state === "isolated") entry.state = "normal"
    })
  }

  // ── Selected Elements Panel ──

  renderSelectedList() {
    if (!this.hasSelectedListTarget) return

    if (this.selectedElements.length === 0) {
      this.selectedListTarget.classList.add("hidden")
      this.selectedListTarget.innerHTML = ""
      return
    }

    this.selectedListTarget.classList.remove("hidden")

    const items = this.selectedElements.map(entry => {
      const activeClass = (state) => entry.state === state ? "btn-active btn-primary" : ""
      const annotateClass = entry.annotation ? "btn-active btn-error" : ""

      let stateSliderHtml = ""
      if (entry.state === "dimmed") {
        stateSliderHtml = `
          <div class="flex items-center gap-2 pt-1">
            <span class="text-[10px] text-gray-400 w-14 shrink-0">Opacity</span>
            <input type="range" min="0" max="1" step="0.05" value="${entry.dimOpacity}"
                   class="range range-xs range-primary flex-1"
                   data-pick-id="${entry.id}"
                   data-action="input->marketing-screenshot-studio#updateDimOpacity" />
            <span class="text-[10px] text-gray-400 w-8 text-right">${entry.dimOpacity}</span>
          </div>`
      } else if (entry.state === "isolated") {
        stateSliderHtml = `
          <div class="flex items-center gap-2 pt-1">
            <span class="text-[10px] text-gray-400 w-14 shrink-0">Opacity</span>
            <input type="range" min="0" max="1" step="0.05" value="${entry.isolateOpacity}"
                   class="range range-xs range-primary flex-1"
                   data-pick-id="${entry.id}"
                   data-action="input->marketing-screenshot-studio#updateIsolateOpacity" />
            <span class="text-[10px] text-gray-400 w-8 text-right">${entry.isolateOpacity}</span>
          </div>`
      } else if (entry.state === "lifted") {
        stateSliderHtml = `
          <div class="flex items-center gap-2 pt-1">
            <span class="text-[10px] text-gray-400 w-14 shrink-0">Elevation</span>
            <input type="range" min="0" max="50" step="1" value="${entry.liftAmount}"
                   class="range range-xs range-primary flex-1"
                   data-pick-id="${entry.id}"
                   data-action="input->marketing-screenshot-studio#updateLiftAmount" />
            <span class="text-[10px] text-gray-400 w-8 text-right">${entry.liftAmount}</span>
          </div>`
      }

      let textEditHtml = ""
      if (entry.editing) {
        const currentText = entry.element.textContent || ""
        textEditHtml = `
          <div class="pt-1 space-y-1">
            <textarea data-text-edit class="textarea textarea-bordered textarea-xs w-full font-mono text-[10px]"
                      rows="3">${this.escapeHtml(currentText)}</textarea>
            <button class="btn btn-primary btn-xs w-full"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#saveElementText">Save Text</button>
          </div>`
      }

      return `
        <div class="bg-base-200 rounded-lg px-3 py-2 space-y-2">
          <div class="flex items-center gap-2 text-xs">
            <span class="font-mono truncate flex-1" title="${this.escapeHtml(entry.label)}">${this.escapeHtml(entry.label)}</span>
            <button class="btn btn-ghost btn-xs text-error"
                    title="Deselect" data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#removePickedElement">&times;</button>
          </div>
          <div class="flex gap-1 flex-wrap">
            <button class="btn btn-ghost btn-xs ${activeClass("hidden")}"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#hideElement">Hide</button>
            <button class="btn btn-ghost btn-xs ${activeClass("dimmed")}"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#dimElement">Dim</button>
            <button class="btn btn-ghost btn-xs ${activeClass("isolated")}"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#isolateElement">Isolate</button>
            <button class="btn btn-ghost btn-xs ${activeClass("lifted")}"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#liftElement">Lift</button>
            <button class="btn btn-ghost btn-xs ${annotateClass}"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#annotateElement">Annotate</button>
          </div>${stateSliderHtml}
          <div class="flex gap-1 flex-wrap pt-1 border-t border-base-300">
            <button class="btn btn-ghost btn-xs"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#editElementText">Edit Text</button>
            <button class="btn btn-ghost btn-xs"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#duplicateElement">Duplicate</button>
            <button class="btn btn-ghost btn-xs text-error"
                    data-pick-id="${entry.id}"
                    data-action="click->marketing-screenshot-studio#removeFromDom">Delete</button>
          </div>${textEditHtml}
          <div class="space-y-1 pt-1 border-t border-base-300">
            <div class="flex items-center gap-2">
              <span class="text-[10px] text-gray-400 w-14 shrink-0">BG Color</span>
              <input type="color" value="#ffffff"
                     class="w-6 h-6 rounded cursor-pointer border-0 p-0"
                     data-pick-id="${entry.id}"
                     data-action="input->marketing-screenshot-studio#overrideBackground" />
            </div>
            <div class="flex items-center gap-2">
              <span class="text-[10px] text-gray-400 w-14 shrink-0">Text Color</span>
              <input type="color" value="#000000"
                     class="w-6 h-6 rounded cursor-pointer border-0 p-0"
                     data-pick-id="${entry.id}"
                     data-action="input->marketing-screenshot-studio#overrideTextColor" />
            </div>
            <div class="flex items-center gap-2">
              <span class="text-[10px] text-gray-400 w-14 shrink-0">Radius</span>
              <input type="range" min="0" max="50" step="1" value="0"
                     class="range range-xs range-primary flex-1"
                     data-pick-id="${entry.id}"
                     data-action="input->marketing-screenshot-studio#overrideBorderRadius" />
              <span class="text-[10px] text-gray-400 w-10 text-right">0px</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-[10px] text-gray-400 w-14 shrink-0">Padding</span>
              <input type="range" min="0" max="64" step="1" value="0"
                     class="range range-xs range-primary flex-1"
                     data-pick-id="${entry.id}"
                     data-action="input->marketing-screenshot-studio#overridePadding" />
              <span class="text-[10px] text-gray-400 w-10 text-right">0px</span>
            </div>
          </div>
        </div>`
    }).join("")

    this.selectedListTarget.innerHTML = `
      <div class="divider text-xs text-gray-400">Selected Elements</div>
      <div class="space-y-2">${items}</div>
    `
  }

  // ── HTML Tree Panel ──

  showHtmlTree() {
    if (!this.hasHtmlTreeTarget) return
    this.htmlTreeTarget.classList.remove("hidden")
    this.htmlTreeTarget.classList.add("flex")
    if (this.hasResizeHandleTarget) {
      this.resizeHandleTarget.classList.remove("hidden")
    }
  }

  hideHtmlTree() {
    if (!this.hasHtmlTreeTarget) return
    this.htmlTreeTarget.classList.add("hidden")
    this.htmlTreeTarget.classList.remove("flex")
    if (this.hasHtmlTreeBodyTarget) {
      this.htmlTreeBodyTarget.innerHTML = ""
    }
    if (this.hasResizeHandleTarget) {
      this.resizeHandleTarget.classList.add("hidden")
    }
    this.treeNodeMap = new WeakMap()
    this.treeElementMap = new WeakMap()
    this.treeIdCounter = 0
  }

  buildHtmlTree() {
    const doc = this.getIframeDoc()
    if (!doc || !this.hasHtmlTreeBodyTarget) return

    const content = doc.querySelector(".cinematic-content")
    if (!content) return

    this.treeNodeMap = new WeakMap()
    this.treeElementMap = new WeakMap()
    this.treeIdCounter = 0

    const fragment = document.createDocumentFragment()

    Array.from(content.childNodes).forEach(child => {
      const node = this.buildTreeNode(child, 0)
      if (node) fragment.appendChild(node)
    })

    this.htmlTreeBodyTarget.innerHTML = ""
    this.htmlTreeBodyTarget.appendChild(fragment)
  }

  buildTreeNode(domNode, depth) {
    // Skip text nodes that are whitespace-only
    if (domNode.nodeType === Node.TEXT_NODE) {
      const text = domNode.textContent.trim()
      if (!text) return null

      const textDiv = document.createElement("div")
      textDiv.className = "tree-line flex items-start px-1"
      const truncated = text.length > 60 ? text.substring(0, 60) + "..." : text
      textDiv.innerHTML = `<span class="w-4 shrink-0 inline-block"></span><span class="text-gray-300 italic truncate">"${this.escapeHtml(truncated)}"</span>`
      return textDiv
    }

    // Skip comment nodes and non-element nodes
    if (domNode.nodeType !== Node.ELEMENT_NODE) return null

    // Skip picker infrastructure
    if (this.isPickerInfrastructure(domNode)) return null

    const treeId = String(++this.treeIdCounter)
    const tag = domNode.tagName.toLowerCase()
    const hasChildren = domNode.childNodes.length > 0 && this.hasVisibleChildren(domNode)
    const isSelfClosing = ["br", "hr", "img", "input", "meta", "link", "area", "base", "col", "embed", "source", "track", "wbr"].includes(tag)

    // Build attribute string
    const attrsHtml = this.buildAttributesHtml(domNode)

    const container = document.createElement("div")
    container.className = "tree-node"
    container.dataset.treeId = treeId

    // Store bidirectional mapping
    this.treeNodeMap.set(domNode, container)
    this.treeElementMap.set(container, domNode)

    // Opening tag line
    const openLine = document.createElement("div")
    openLine.className = "tree-line flex items-start hover:bg-slate-700/50 px-1 rounded cursor-pointer"
    openLine.dataset.treeId = treeId

    if (hasChildren && !isSelfClosing) {
      const expanded = depth < 2
      openLine.innerHTML = `<span class="tree-toggle w-4 shrink-0 text-gray-500 cursor-pointer select-none text-center" data-tree-id="${treeId}">${expanded ? "&#9660;" : "&#9654;"}</span><span><span class="text-gray-500">&lt;</span><span class="text-pink-400">${this.escapeHtml(tag)}</span>${attrsHtml}<span class="text-gray-500">&gt;</span></span>`

      container.appendChild(openLine)

      // Children container
      const childrenDiv = document.createElement("div")
      childrenDiv.className = `tree-children pl-4${expanded ? "" : " hidden"}`
      childrenDiv.dataset.treeId = treeId

      Array.from(domNode.childNodes).forEach(child => {
        const childNode = this.buildTreeNode(child, depth + 1)
        if (childNode) childrenDiv.appendChild(childNode)
      })
      container.appendChild(childrenDiv)

      // Closing tag line
      const closeLine = document.createElement("div")
      closeLine.className = `tree-line px-1${expanded ? "" : " hidden"}`
      closeLine.dataset.treeCloseId = treeId
      closeLine.innerHTML = `<span class="w-4 shrink-0 inline-block"></span><span class="text-gray-500">&lt;/</span><span class="text-pink-400">${this.escapeHtml(tag)}</span><span class="text-gray-500">&gt;</span>`
      container.appendChild(closeLine)
    } else if (isSelfClosing) {
      openLine.innerHTML = `<span class="w-4 shrink-0 inline-block"></span><span><span class="text-gray-500">&lt;</span><span class="text-pink-400">${this.escapeHtml(tag)}</span>${attrsHtml}<span class="text-gray-500"> /&gt;</span></span>`
      container.appendChild(openLine)
    } else {
      // Element with no visible children (may have text content)
      const textContent = domNode.textContent.trim()
      const truncated = textContent.length > 40 ? textContent.substring(0, 40) + "..." : textContent
      const textPart = textContent ? `<span class="text-gray-300">${this.escapeHtml(truncated)}</span>` : ""
      openLine.innerHTML = `<span class="w-4 shrink-0 inline-block"></span><span><span class="text-gray-500">&lt;</span><span class="text-pink-400">${this.escapeHtml(tag)}</span>${attrsHtml}<span class="text-gray-500">&gt;</span>${textPart}<span class="text-gray-500">&lt;/</span><span class="text-pink-400">${this.escapeHtml(tag)}</span><span class="text-gray-500">&gt;</span></span>`
      container.appendChild(openLine)
    }

    // Add event listeners for hover and click
    openLine.addEventListener("mouseenter", () => this.onTreeNodeHover(domNode))
    openLine.addEventListener("mouseleave", () => this.onTreeNodeHoverOut(domNode))
    openLine.addEventListener("click", (e) => {
      // If clicking the toggle arrow, toggle instead of picking
      if (e.target.classList.contains("tree-toggle")) {
        this.toggleTreeNodeById(treeId)
        return
      }
      this.onTreeNodeClick(domNode)
    })

    return container
  }

  buildAttributesHtml(el) {
    if (!el.attributes || el.attributes.length === 0) return ""

    let html = ""
    const maxAttrs = 3
    const attrs = Array.from(el.attributes).filter(a => !a.name.startsWith("data-screenshot-"))

    attrs.slice(0, maxAttrs).forEach(attr => {
      const value = attr.value.length > 50 ? attr.value.substring(0, 50) + "..." : attr.value
      html += `<span class="text-purple-400"> ${this.escapeHtml(attr.name)}</span><span class="text-gray-500">=</span><span class="text-sky-400">"${this.escapeHtml(value)}"</span>`
    })

    if (attrs.length > maxAttrs) {
      html += `<span class="text-gray-500"> ...+${attrs.length - maxAttrs}</span>`
    }

    return html
  }

  hasVisibleChildren(el) {
    return Array.from(el.childNodes).some(child => {
      if (child.nodeType === Node.ELEMENT_NODE && !this.isPickerInfrastructure(child)) return true
      if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) return true
      return false
    })
  }

  toggleTreeNodeById(treeId) {
    if (!this.hasHtmlTreeBodyTarget) return

    const children = this.htmlTreeBodyTarget.querySelector(`.tree-children[data-tree-id="${treeId}"]`)
    const closeLine = this.htmlTreeBodyTarget.querySelector(`[data-tree-close-id="${treeId}"]`)
    const toggle = this.htmlTreeBodyTarget.querySelector(`.tree-toggle[data-tree-id="${treeId}"]`)

    if (!children) return

    const isHidden = children.classList.contains("hidden")
    if (isHidden) {
      children.classList.remove("hidden")
      if (closeLine) closeLine.classList.remove("hidden")
      if (toggle) toggle.innerHTML = "&#9660;"
    } else {
      children.classList.add("hidden")
      if (closeLine) closeLine.classList.add("hidden")
      if (toggle) toggle.innerHTML = "&#9654;"
    }
  }

  collapseAllTree() {
    if (!this.hasHtmlTreeBodyTarget) return
    this.htmlTreeBodyTarget.querySelectorAll(".tree-children").forEach(el => el.classList.add("hidden"))
    this.htmlTreeBodyTarget.querySelectorAll("[data-tree-close-id]").forEach(el => el.classList.add("hidden"))
    this.htmlTreeBodyTarget.querySelectorAll(".tree-toggle").forEach(el => el.innerHTML = "&#9654;")
  }

  expandAllTree() {
    if (!this.hasHtmlTreeBodyTarget) return
    this.htmlTreeBodyTarget.querySelectorAll(".tree-children").forEach(el => el.classList.remove("hidden"))
    this.htmlTreeBodyTarget.querySelectorAll("[data-tree-close-id]").forEach(el => el.classList.remove("hidden"))
    this.htmlTreeBodyTarget.querySelectorAll(".tree-toggle").forEach(el => el.innerHTML = "&#9660;")
  }

  // Tree -> iframe hover sync
  onTreeNodeHover(iframeEl) {
    if (!iframeEl || this.isPickerInfrastructure(iframeEl)) return
    iframeEl.classList.add("screenshot-picker-hover")
  }

  onTreeNodeHoverOut(iframeEl) {
    if (!iframeEl) return
    iframeEl.classList.remove("screenshot-picker-hover")
  }

  // Tree -> iframe click-to-pick
  onTreeNodeClick(iframeEl) {
    if (!iframeEl || this.isPickerInfrastructure(iframeEl)) return
    if (iframeEl.dataset.screenshotPickId) return  // Already picked

    const id = String(++this.pickIdCounter)
    iframeEl.dataset.screenshotPickId = id

    this.selectedElements.push({
      id,
      element: iframeEl,
      label: this.getElementLabel(iframeEl),
      state: "normal",
      dimOpacity: 0.15,
      isolateOpacity: 0.08,
      liftAmount: 20
    })

    iframeEl.classList.remove("screenshot-picker-hover")

    // Mark tree node as picked
    const treeNode = this.treeNodeMap.get(iframeEl)
    if (treeNode) {
      const line = treeNode.querySelector(".tree-line")
      if (line) line.classList.add("bg-slate-600/50", "ring-1", "ring-sky-500/30")
    }

    this.renderSelectedList()
  }

  // iframe -> tree hover sync
  highlightTreeNodeForElement(iframeEl) {
    this.unhighlightTreeNode()

    const treeNode = this.treeNodeMap.get(iframeEl)
    if (!treeNode) return

    const line = treeNode.querySelector(".tree-line")
    if (line) {
      line.classList.add("bg-blue-500/20")
      this.highlightedTreeNode = line

      // Expand parent tree nodes to make this visible
      this.expandTreeParents(treeNode)

      // Scroll into view
      line.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }

  unhighlightTreeNode() {
    if (this.highlightedTreeNode) {
      this.highlightedTreeNode.classList.remove("bg-blue-500/20")
      this.highlightedTreeNode = null
    }
  }

  expandTreeParents(treeNode) {
    let current = treeNode.parentElement
    while (current && current !== this.htmlTreeBodyTarget) {
      if (current.classList.contains("tree-children") && current.classList.contains("hidden")) {
        current.classList.remove("hidden")
        const treeId = current.dataset.treeId
        if (treeId) {
          const closeLine = this.htmlTreeBodyTarget.querySelector(`[data-tree-close-id="${treeId}"]`)
          const toggle = this.htmlTreeBodyTarget.querySelector(`.tree-toggle[data-tree-id="${treeId}"]`)
          if (closeLine) closeLine.classList.remove("hidden")
          if (toggle) toggle.innerHTML = "&#9660;"
        }
      }
      current = current.parentElement
    }
  }

  // ── Resize Handle ──

  startResize(e) {
    e.preventDefault()
    this.resizing = true
    document.addEventListener("mousemove", this.boundOnResizeMove)
    document.addEventListener("mouseup", this.boundOnResizeEnd)
  }

  onResizeMove(e) {
    if (!this.resizing || !this.hasHtmlTreeTarget || !this.hasPreviewAreaTarget) return

    const container = this.htmlTreeTarget.parentElement
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const newTreeHeight = containerRect.bottom - e.clientY

    // Clamp between 100px and 80% of the container
    const maxHeight = containerRect.height * 0.8
    const clamped = Math.max(100, Math.min(newTreeHeight, maxHeight))

    this.htmlTreeTarget.style.height = clamped + "px"
  }

  onResizeEnd() {
    this.resizing = false
    document.removeEventListener("mousemove", this.boundOnResizeMove)
    document.removeEventListener("mouseup", this.boundOnResizeEnd)
  }

  // ── Helpers ──

  resetElementStyle(entry) {
    if (entry.state === "hidden") entry.element.style.removeProperty("display")
    if (entry.state === "dimmed") entry.element.style.removeProperty("opacity")
    if (entry.state === "isolated") this.removeIsolation()
    if (entry.state === "lifted") this.removeLift(entry)

    // Restore saved style overrides
    if (entry.savedStyles) {
      Object.entries(entry.savedStyles).forEach(([prop, original]) => {
        const cssProp = prop.replace(/([A-Z])/g, "-$1").toLowerCase()
        if (original) {
          entry.element.style.setProperty(cssProp, original)
        } else {
          entry.element.style.removeProperty(cssProp)
        }
      })
      entry.savedStyles = null
    }

    entry.state = "normal"
  }

  applyLift(entry) {
    const doc = this.getIframeDoc()
    if (!doc) return

    const el = entry.element
    const amount = entry.liftAmount
    const scale = 1 + (amount / 625) // 0 -> 1.0, 50 -> 1.08
    const iframeWindow = this.previewTarget.contentWindow
    const computedStyle = iframeWindow.getComputedStyle(el)
    const elRect = el.getBoundingClientRect()

    // Create a clone overlay positioned fixed in iframe viewport
    const clone = el.cloneNode(true)
    clone.classList.add("screenshot-lift-clone")
    clone.style.cssText = [
      `position: fixed`,
      `left: ${elRect.left}px`,
      `top: ${elRect.top}px`,
      `width: ${elRect.width}px`,
      `height: ${elRect.height}px`,
      `z-index: 99998`,
      `transform: scale(${scale})`,
      `box-shadow: 0 ${amount}px ${amount * 2}px rgba(0,0,0,0.4)`,
      `transition: transform 0.3s ease, box-shadow 0.3s ease`,
      `pointer-events: none`,
      `border-radius: ${computedStyle.borderRadius}`,
      `overflow: hidden`,
      `background: ${computedStyle.backgroundColor}`,
      `margin: 0`,
      `padding: ${computedStyle.padding}`
    ].map(s => s + " !important").join("; ")

    // Dim the original element
    el.style.setProperty("opacity", "0.3", "important")

    doc.body.appendChild(clone)
    entry.liftClone = clone
  }

  removeLift(entry) {
    entry.element.style.removeProperty("opacity")
    if (entry.liftClone) {
      entry.liftClone.remove()
      entry.liftClone = null
    }
  }

  clearSelections() {
    this.selectedElements.forEach(entry => {
      if (entry.liftClone) entry.liftClone = null
      if (entry.annotation) entry.annotation = null
    })
    this.selectedElements = []
    this.renderSelectedList()
  }

  getIframeDoc() {
    try {
      return this.previewTarget.contentDocument
    } catch (e) {
      return null
    }
  }

  isPickerInfrastructure(el) {
    return el.classList?.contains("screenshot-hover-label") ||
           el.classList?.contains("screenshot-lift-clone") ||
           el.classList?.contains("screenshot-annotation") ||
           el.classList?.contains("cinematic-stage") ||
           el.classList?.contains("cinematic-frame") ||
           el.classList?.contains("cinematic-grain") ||
           el.tagName === "HTML" ||
           el.tagName === "BODY"
  }

  getElementLabel(el) {
    const tag = el.tagName.toLowerCase()
    const classes = Array.from(el.classList)
      .filter(c => !c.startsWith("screenshot-"))
      .slice(0, 3)
      .join(".")
    return classes ? `${tag}.${classes}` : tag
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // ── Scene Params ──

  appendSceneParams(formData) {
    formData.append("scene", this.scenePresetTarget.value)
    formData.append("frame_width", this.frameWidthTarget.value)
    formData.append("frame_height", this.frameHeightTarget.value)

    if (this.hasPerspectiveTarget) {
      formData.append("perspective", this.presetPerspectiveRaw === "none" ? "none" : this.perspectiveTarget.value + "px")
    }
    if (this.hasRotateXTarget) formData.append("rotate_x", this.rotateXTarget.value + "deg")
    if (this.hasRotateYTarget) formData.append("rotate_y", this.rotateYTarget.value + "deg")
    if (this.hasScaleTarget) formData.append("scale", this.scaleTarget.value)
    if (this.hasGrainTarget) formData.append("grain_opacity", this.grainTarget.value)
    if (this.customShadow) formData.append("shadow", this.customShadow)
  }

  appendSceneSearchParams(params) {
    params.set("scene", this.scenePresetTarget.value)
    params.set("frame_width", this.frameWidthTarget.value)
    params.set("frame_height", this.frameHeightTarget.value)

    if (this.hasPerspectiveTarget) {
      params.set("perspective", this.presetPerspectiveRaw === "none" ? "none" : this.perspectiveTarget.value + "px")
    }
    if (this.hasRotateXTarget) params.set("rotate_x", this.rotateXTarget.value + "deg")
    if (this.hasRotateYTarget) params.set("rotate_y", this.rotateYTarget.value + "deg")
    if (this.hasScaleTarget) params.set("scale", this.scaleTarget.value)
    if (this.hasGrainTarget) params.set("grain_opacity", this.grainTarget.value)
    if (this.customShadow) params.set("shadow", this.customShadow)
  }

  showPreview() {
    this.previewTarget.style.display = "block"
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.style.display = "none"
    }
  }

  updateSliderValue(event) {
    const target = event.target
    const name = target.dataset.marketingScreenshotStudioTarget

    if (name === "perspective") this.presetPerspectiveRaw = null

    const valueTargetMap = {
      perspective: "perspectiveValue",
      rotateX: "rotateXValue",
      rotateY: "rotateYValue",
      scale: "scaleValue",
      grain: "grainValue"
    }

    const valueTarget = valueTargetMap[name]
    if (valueTarget && this[`has${valueTarget.charAt(0).toUpperCase() + valueTarget.slice(1)}Target`]) {
      const suffix = name === "perspective" ? "px"
        : (name === "rotateX" || name === "rotateY") ? "deg"
        : ""
      this[`${valueTarget}Target`].textContent = target.value + suffix
    }
  }

  openPreviewTab() {
    if (this.currentMode === "html" && this.previewTarget.srcdoc) {
      const blob = new Blob([this.previewTarget.srcdoc], { type: "text/html" })
      window.open(URL.createObjectURL(blob), "_blank")
    } else {
      const src = this.previewTarget.src
      if (src && src !== "about:blank") {
        window.open(src, "_blank")
      }
    }
  }
}
